import React, { useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';

// ── JSON Schema — mirrors SelfServiceLoanProductItem exactly ──────────────────
const LOAN_PRODUCT_SCHEMA = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  required: ['loanProducts'],
  properties: {
    application: {
      type: 'object',
      description: 'Application metadata',
      additionalProperties: false,
      properties: {
        name:          { type: 'string', description: 'Display name of this config' },
        code:          { type: 'string', description: 'Unique code identifier' },
        syncSignature: { type: 'string', description: 'Sync signature for validation' }
      }
    },
    loanProducts: {
      type: 'array',
      description: 'List of loan products available to self-service customers',
      minItems: 1,
      items: {
        type: 'object',
        additionalProperties: false,
        required: ['name', 'code', 'currencyCode', 'interestRate',
                   'interestRateCalculationPeriod', 'interestRateCalculationsType',
                   'disbursementFeeCalculation', 'disbursementFee',
                   'minimumAmount', 'maximumAmount', 'maximumTenorInDays'],
        properties: {
          name: {
            type: 'string',
            description: 'Display name of the loan product'
          },
          code: {
            type: 'string',
            description: 'Unique product code — must match the core banking product code'
          },
          currencyCode: {
            type: 'string',
            description: 'ISO 4217 currency code',
            enum: ['NGN', 'USD', 'GBP', 'EUR']
          },
          interestRate: {
            type: 'number',
            description: 'Interest rate value. E.g. 2.5 means 2.5%',
            minimum: 0,
            maximum: 100
          },
          interestRateCalculationPeriod: {
            type: 'string',
            description: 'Period over which interest is calculated',
            enum: ['daily', 'weekly', 'monthly', 'yearly']
          },
          interestRateCalculationDescription: {
            type: 'string',
            description: 'Human-readable description shown to the customer (e.g. "2.5% per month")'
          },
          interestRateCalculationsType: {
            type: 'string',
            description: 'Method used to calculate interest',
            enum: ['flat', 'reducingBalance']
          },
          disbursementFeeCalculation: {
            type: 'string',
            description: 'How the disbursement fee is computed',
            enum: ['flat', 'percentageLoanAmount', 'percentageDisbursedAmount']
          },
          disbursementFeeCalculationDescription: {
            type: 'string',
            description: 'Human-readable description of the disbursement fee'
          },
          disbursementFee: {
            type: 'number',
            description: 'Fee amount (if flat) or percentage value (if percentage type)',
            minimum: 0
          },
          minimumAmount: {
            type: 'number',
            description: 'Minimum loan amount allowed',
            minimum: 0
          },
          maximumAmount: {
            type: 'number',
            description: 'Maximum loan amount allowed',
            minimum: 0
          },
          maximumTenorInDays: {
            type: 'integer',
            description: 'Maximum loan duration in days',
            minimum: 1
          }
        }
      }
    }
  }
};

const DEFAULT_YAML = `application:
  name: Loan Product Configuration
  code: loanProductConfiguration
  syncSignature: ""

loanProducts:
  - name: Quick Loan
    code: QUICK_LOAN_001
    currencyCode: NGN
    interestRate: 2.5
    interestRateCalculationPeriod: monthly
    interestRateCalculationDescription: 2.5% per month
    interestRateCalculationsType: reducingBalance
    disbursementFeeCalculation: flat
    disbursementFeeCalculationDescription: Flat fee of 500
    disbursementFee: 500
    minimumAmount: 10000
    maximumAmount: 500000
    maximumTenorInDays: 90
`;

// This runs once — safe to call multiple times due to internal guard
let completionProviderRegistered = false;

/**
 * Walk backwards from the cursor to determine:
 *  - `node`          — the schema node that applies at the cursor position
 *  - `isNewArrayItem`— true when the cursor is at the "- " level of a list
 *                      (i.e. about to start a new item, not inside an existing one)
 */
function getSchemaContext(model, position, schema) {
  const currentLineContent = model.getLineContent(position.lineNumber);
  const currentIndent = (currentLineContent.match(/^(\s*)/) || ['', ''])[1].length;

  const ancestors = [];
  let targetIndent = currentIndent;
  let encounteredListItem = false; // true once we walk through an existing list item line

  for (let ln = position.lineNumber - 1; ln >= 1; ln--) {
    const line = model.getLineContent(ln);
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;

    const indent = (line.match(/^(\s*)/) || ['', ''])[1].length;
    if (indent >= targetIndent) continue; // same/deeper level — not a parent

    const isListItem = trimmed.startsWith('-');

    if (isListItem) {
      const afterDash = trimmed.slice(1).trimStart();
      // "- key: value" — first property of a list item; NOT a container
      const inlineKeyValue = afterDash.match(/^(\w[\w-]*)\s*:\s*\S/);
      if (inlineKeyValue) {
        encounteredListItem = true;
        targetIndent = indent;
        continue;
      }
      // "- key:" (block key only) — treat as a container ancestor
      const blockKey = afterDash.match(/^(\w[\w-]*)\s*:/);
      if (blockKey) {
        ancestors.unshift(blockKey[1]);
        encounteredListItem = true;
      }
      targetIndent = indent;
    } else {
      const m = trimmed.match(/^(\w[\w-]*)\s*:/);
      if (m) {
        ancestors.unshift(m[1]);
        targetIndent = indent;
      }
    }

    if (targetIndent === 0) break;
  }

  // Navigate schema along the ancestor path
  let node = schema;
  let steppedThroughArray = false;
  for (const key of ancestors) {
    if (node?.properties?.[key]) {
      node = node.properties[key];
      if (node.type === 'array' && node.items) {
        node = node.items;
        steppedThroughArray = true;
      }
    } else {
      return { node: null, isNewArrayItem: false };
    }
  }

  // Cursor is at the level where "- " items start (parent is an array,
  // but we have NOT walked through an existing list item line)
  const isNewArrayItem = steppedThroughArray && !encounteredListItem;

  return { node, isNewArrayItem };
}

/**
 * Collect the property keys already present in the current YAML block
 * so we can hide them from the suggestions.
 */
function getUsedKeysInCurrentBlock(model, position) {
  const currentLineContent = model.getLineContent(position.lineNumber);
  const currentIndent = (currentLineContent.match(/^(\s*)/) || ['', ''])[1].length;
  const totalLines = model.getLineCount();
  const usedKeys = new Set();

  // Find where the current block opens (first line with strictly lower indent)
  let blockStartLine = 1;
  for (let ln = position.lineNumber - 1; ln >= 1; ln--) {
    const line = model.getLineContent(ln);
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const indent = (line.match(/^(\s*)/) || ['', ''])[1].length;
    if (indent < currentIndent) { blockStartLine = ln; break; }
  }

  // Scan within the block for keys at the current indent level
  for (let ln = blockStartLine; ln <= totalLines; ln++) {
    const line = model.getLineContent(ln);
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const indent = (line.match(/^(\s*)/) || ['', ''])[1].length;
    // Stop when we leave the block
    if (indent < currentIndent && ln > blockStartLine) break;
    if (indent === currentIndent) {
      const noMarker = trimmed.startsWith('-') ? trimmed.slice(1).trimStart() : trimmed;
      const m = noMarker.match(/^(\w[\w-]*)\s*:/);
      if (m) usedKeys.add(m[1]);
    }
  }

  return usedKeys;
}

// ── Enum validation ───────────────────────────────────────────────────────────
// Build a flat map of  fieldName → allowedEnumValues  from the whole schema
function buildEnumMap(schema) {
  const map = {};
  function walk(node) {
    if (!node?.properties) return;
    Object.entries(node.properties).forEach(([key, prop]) => {
      if (prop.enum) map[key] = prop.enum;
      if (prop.properties) walk(prop);
      if (prop.items) walk(prop.items);
    });
  }
  walk(schema);
  return map;
}

const ENUM_MAP = buildEnumMap(LOAN_PRODUCT_SCHEMA);

// Build required-key set from loanProducts items for sorting in suggestions
const ITEM_REQUIRED = new Set(
  LOAN_PRODUCT_SCHEMA.properties.loanProducts.items.required || []
);

function validateEnums(model, monaco) {
  const lines = model.getLinesContent();
  const markers = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;

    // Match "- key: value" or "key: value"
    const m = trimmed.match(/^-?\s*(\w[\w-]*)\s*:\s*(\S.*)/);
    if (!m) return;

    const [, key, rawValue] = m;
    const value = rawValue.trim();
    if (!ENUM_MAP[key] || !value) return;

    if (!ENUM_MAP[key].includes(value)) {
      const colonCol = line.indexOf(':');
      const afterColon = line.slice(colonCol + 1);
      const valueOffset = afterColon.search(/\S/);
      const startCol = colonCol + 1 + valueOffset + 1; // 1-based

      markers.push({
        severity: monaco.MarkerSeverity.Error,
        message: `"${value}" is not a valid value for "${key}".\nAllowed: ${ENUM_MAP[key].map((v) => `"${v}"`).join(', ')}`,
        startLineNumber: i + 1,
        startColumn: startCol,
        endLineNumber: i + 1,
        endColumn: startCol + value.length,
      });
    }
  });

  return markers;
}

export default function LoanProductYamlEditor({ value = DEFAULT_YAML, onChange }) {
  const editorRef = useRef(null);

  const handleBeforeMount = useCallback((monaco) => {
    if (completionProviderRegistered) return;
    completionProviderRegistered = true;

    // ── 1. Completion provider ────────────────────────────────────────────────
    monaco.languages.registerCompletionItemProvider('yaml', {
      // Auto-trigger after typing a space (catches "key: <space>" and "- <space>")
      triggerCharacters: [' ', '-'],
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn,
        };

        const lineContent = model.getLineContent(position.lineNumber);
        const linePrefix = lineContent.substring(0, position.column - 1);
        const colonIdx = linePrefix.indexOf(':');
        const isValuePosition = colonIdx !== -1;

        const { node: contextNode, isNewArrayItem } = getSchemaContext(
          model, position, LOAN_PRODUCT_SCHEMA
        );

        if (isValuePosition) {
          // ── Enum value completions ────────────────────────────────────────
          const keyMatch = linePrefix.match(/(\w+)\s*:/);
          const currentKey = keyMatch?.[1];
          const prop = currentKey && contextNode?.properties?.[currentKey];
          if (!prop?.enum) return { suggestions: [] };

          return {
            suggestions: prop.enum.map((val) => ({
              label: String(val),
              kind: monaco.languages.CompletionItemKind.Enum,
              detail: prop.description || currentKey,
              documentation: {
                value: [
                  `**${currentKey}**`,
                  prop.description ? `\n\n${prop.description}` : '',
                  `\n\n*Allowed values:* ${prop.enum.map((v) => `\`${v}\``).join(', ')}`,
                ].join(''),
                isTrusted: true,
              },
              insertText: String(val),
              range,
            })),
          };
        } else {
          // ── Property key completions ──────────────────────────────────────
          if (!contextNode?.properties) return { suggestions: [] };

          const usedKeys = isNewArrayItem
            ? new Set()
            : getUsedKeysInCurrentBlock(model, position);

          const trimmedLine = lineContent.trimStart();
          const alreadyHasDash = trimmedLine.startsWith('-');
          const needsDash = isNewArrayItem && !alreadyHasDash;

          const entries = Object.entries(contextNode.properties).filter(
            ([key]) => !usedKeys.has(key)
          );

          // Sort: required fields first, then alphabetical
          entries.sort(([a], [b]) => {
            const aReq = ITEM_REQUIRED.has(a) ? 0 : 1;
            const bReq = ITEM_REQUIRED.has(b) ? 0 : 1;
            return aReq - bReq || a.localeCompare(b);
          });

          return {
            suggestions: entries.map(([key, prop], idx) => {
              const isRequired = ITEM_REQUIRED.has(key);
              const label = needsDash ? `- ${key}` : key;
              const insertText = needsDash ? `- ${key}: ` : `${key}: `;

              const docParts = [`**${key}**${isRequired ? '  *(required)*' : ''}`];
              if (prop.description) docParts.push(`\n\n${prop.description}`);
              if (prop.type) docParts.push(`\n\n*Type:* \`${prop.type}\``);
              if (prop.enum) {
                docParts.push(
                  `\n\n*Allowed values:* ${prop.enum.map((v) => `\`${v}\``).join(', ')}`
                );
              }
              if (prop.minimum !== undefined || prop.maximum !== undefined) {
                docParts.push(
                  `\n\n*Range:* \`${prop.minimum ?? '–'}\` – \`${prop.maximum ?? '–'}\``
                );
              }

              return {
                label,
                kind: monaco.languages.CompletionItemKind.Property,
                // Prepend ★ to required fields so they stand out visually
                detail: `${isRequired ? '★ required  ' : ''}${prop.description || key}`,
                documentation: { value: docParts.join(''), isTrusted: true },
                insertText,
                // sortText ensures required fields always sort before optional ones
                sortText: `${idx.toString().padStart(4, '0')}`,
                range,
              };
            }),
          };
        }
      },
    });

    // ── 2. Hover provider ─────────────────────────────────────────────────────
    monaco.languages.registerHoverProvider('yaml', {
      provideHover: (model, position) => {
        const line = model.getLineContent(position.lineNumber);
        const trimmed = line.trim();

        // Find the key on this line (strip list marker)
        const noMarker = trimmed.startsWith('-') ? trimmed.slice(1).trimStart() : trimmed;
        const keyMatch = noMarker.match(/^(\w[\w-]*)\s*:/);
        if (!keyMatch) return null;

        const key = keyMatch[1];

        // Find the schema node at this position to get the right property
        const { node: contextNode } = getSchemaContext(model, position, LOAN_PRODUCT_SCHEMA);
        const prop = contextNode?.properties?.[key];
        if (!prop) return null;

        const isRequired = ITEM_REQUIRED.has(key);
        const hoverParts = [`### \`${key}\`${isRequired ? '  *(required)*' : ''}`];
        if (prop.description) hoverParts.push(`\n\n${prop.description}`);
        if (prop.type) hoverParts.push(`\n\n**Type:** \`${prop.type}\``);
        if (prop.enum) {
          hoverParts.push(
            `\n\n**Allowed values:** ${prop.enum.map((v) => `\`${v}\``).join(' | ')}`
          );
        }
        if (prop.minimum !== undefined || prop.maximum !== undefined) {
          hoverParts.push(
            `\n\n**Range:** \`${prop.minimum ?? '–'}\` – \`${prop.maximum ?? '–'}\``
          );
        }

        // Highlight only the key token
        const keyStart = line.indexOf(key);
        return {
          range: new monaco.Range(
            position.lineNumber, keyStart + 1,
            position.lineNumber, keyStart + key.length + 1
          ),
          contents: [{ value: hoverParts.join(''), isTrusted: true }],
        };
      },
    });
  }, []);

  // ── 3. Enum validation on content change ───────────────────────────────────
  const handleMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    const model = editor.getModel();

    const runValidation = () => {
      const markers = validateEnums(model, monaco);
      monaco.editor.setModelMarkers(model, 'yaml-schema', markers);
    };

    // Validate immediately and on every change (debounced 600 ms)
    runValidation();
    let timer;
    model.onDidChangeContent(() => {
      clearTimeout(timer);
      timer = setTimeout(runValidation, 600);
    });
  }, []);

  return (
    <Editor
      height="600px"
      path="loanProductConfiguration.yaml"
      language="yaml"
      theme="vs-dark"
      value={value}
      onChange={onChange}
      beforeMount={handleBeforeMount}
      onMount={handleMount}
      options={{
        fontSize: 14,
        minimap: { enabled: false },
        automaticLayout: true,
        scrollBeyondLastLine: false,
        wordWrap: 'on',
        suggest: {
          showWords: false,
        },
      }}
    />
  );
}
